import React, { useState, useEffect, useCallback } from "react";
import JSZip from "jszip";
import CustomHeader from "../../components/header/index.jsx";
import PageHeader from "../../components/pageHeader";
import url from "../../config.js";
import "./superadmin.scss";

const domain = url.domain;

const authHeaders = () => ({
  "Content-Type": "application/json",
  "x-access-token": localStorage.getItem("token") || "",
});

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const daysLeft = (end) => {
  if (!end) return null;
  const ms = new Date(end).getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
};

function SuperAdmin() {
  const [plans, setPlans] = useState({});          // { key: {label, maxUsers, durationDays} }
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Add-tenant form
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [plan, setPlan] = useState("free");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminAuthProvider, setAdminAuthProvider] = useState("local"); // 'local' | 'google'
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  // Per-row plan management
  const [rowBusy, setRowBusy] = useState({});
  const [planChoice, setPlanChoice] = useState({});

  // Users (all tenants) — super-admin "move user to tenant"
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [userRowBusy, setUserRowBusy] = useState({});
  const [tenantChoice, setTenantChoice] = useState({}); // userId → target tenantId
  const [userFilter, setUserFilter] = useState("");

  // Which section is shown (Tenants / Users / Session Logs / Token Usage)
  const [activeTab, setActiveTab] = useState("tenants");

  // Token Usage (cross-tenant cost/usage by tenant and by team)
  const _ymd = (d) => d.toISOString().slice(0, 10);
  const _monthStart = () => { const d = new Date(); d.setDate(1); return _ymd(d); };
  const [tokenFrom, setTokenFrom] = useState(_monthStart());
  const [tokenTo, setTokenTo] = useState(_ymd(new Date()));
  const [tokenData, setTokenData] = useState(null);   // { range, totals, byTenant, byTeam }
  const [tokenLoading, setTokenLoading] = useState(false);
  const [tokenErr, setTokenErr] = useState("");
  const [tokenView, setTokenView] = useState("tenant"); // inner tab: "tenant" | "team"

  const loadTenants = useCallback(() => {
    setLoading(true);
    fetch(`${domain}/api/v1/tenants/all`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setTenants(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    fetch(`${domain}/api/v1/tenant/all-users`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(e => setUsersError(e.message))
      .finally(() => setUsersLoading(false));
  }, []);

  useEffect(() => {
    fetch(`${domain}/api/v1/subscription-plans`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        setPlans(data || {});
        const first = Object.keys(data || {})[0];
        if (first) setPlan(first);
      })
      .catch(() => {});
    loadTenants();
    loadUsers();
  }, [loadTenants, loadUsers]);

  const tenantName = useCallback((id) => {
    const t = tenants.find(x => String(x._id) === String(id));
    return t ? (t.name || id) : (id || "—");
  }, [tenants]);

  const fmtTokens = (n) => {
    const v = Number(n) || 0;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return String(v);
  };
  const fmtCost = (n) => `$${(Number(n) || 0).toFixed(4)}`;

  const loadTokenUsage = useCallback((from, to) => {
    setTokenLoading(true);
    setTokenErr("");
    const qs = new URLSearchParams();
    if (from) qs.set("from", from);
    if (to) qs.set("to", to);
    fetch(`${domain}/api/v1/tenants/token-usage?${qs.toString()}`, { headers: authHeaders() })
      .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || `Failed (${r.status})`); return d; })
      .then(data => setTokenData(data))
      .catch(e => setTokenErr(e.message))
      .finally(() => setTokenLoading(false));
  }, []);

  const moveUser = async (userId) => {
    const target = tenantChoice[userId];
    if (!target) { alert("Pick a target tenant first."); return; }
    const user = users.find(u => String(u._id) === String(userId));
    if (user && String(user.tenantId) === String(target)) { alert("User is already in that tenant."); return; }
    if (!window.confirm(`Move ${user ? (user.email || user.username) : "this user"} to "${tenantName(target)}"?`)) return;
    setUserRowBusy((p) => ({ ...p, [userId]: true }));
    try {
      const res = await fetch(`${domain}/api/v1/tenant/users/${userId}/tenant`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ tenantId: target }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Move failed.");
      setTenantChoice((p) => { const n = { ...p }; delete n[userId]; return n; });
      loadUsers();
      loadTenants(); // seat counts change
    } catch (e) {
      alert(e.message);
    } finally {
      setUserRowBusy((p) => { const n = { ...p }; delete n[userId]; return n; });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    if (!name.trim()) { setFormError("Tenant name is required."); return; }
    const emailRe = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;
    if (!emailRe.test(adminEmail.trim())) { setFormError("Enter a valid tenant-admin email."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${domain}/api/v1/tenants`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ name: name.trim(), description: description.trim(), plan, adminEmail: adminEmail.trim(), authProvider: adminAuthProvider }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Could not create tenant.");
      if (data.inviteError) {
        setFormSuccess(`Tenant "${name.trim()}" created, but the admin invite failed: ${data.inviteError}`);
      } else {
        setFormSuccess(`Tenant "${name.trim()}" created and an invite was sent to ${adminEmail.trim()}.`);
      }
      setName(""); setDescription(""); setAdminEmail("");
      loadTenants();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const patchTenant = async (id, body) => {
    setRowBusy((p) => ({ ...p, [id]: true }));
    try {
      const res = await fetch(`${domain}/api/v1/tenants/${id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Update failed.");
      loadTenants();
    } catch (e) {
      alert(e.message);
    } finally {
      setRowBusy((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const statusChip = (t) => {
    const dl = daysLeft(t.subscriptionEnd);
    let mod = "ok", text = t.status || "active";
    if (t.status === "suspended" || t.status === "expired" || (dl != null && dl < 0)) { mod = "err"; text = "expired"; }
    else if (dl != null && dl <= 7) { mod = "warn"; text = `${dl}d left`; }
    else if (dl != null) { text = `${dl}d left`; }
    return <span className={`sa-chip sa-chip--${mod}`}>{text}</span>;
  };

  return (
    <div className="root">
      <CustomHeader />
      <main className="content">
        <div className="sa-page">
          <PageHeader title="Super Admin" subtitle="Provision tenants, choose a subscription plan, and invite each tenant's administrator." />

          {/* Section tabs */}
          <div className="sa-tabs">
            <button className={`sa-tab ${activeTab === "tenants" ? "sa-tab--active" : ""}`} onClick={() => setActiveTab("tenants")}>Tenants</button>
            <button className={`sa-tab ${activeTab === "users" ? "sa-tab--active" : ""}`} onClick={() => setActiveTab("users")}>Users</button>
            <button className={`sa-tab ${activeTab === "logs" ? "sa-tab--active" : ""}`} onClick={() => setActiveTab("logs")}>Session Logs</button>
            <button
              className={`sa-tab ${activeTab === "tokens" ? "sa-tab--active" : ""}`}
              onClick={() => { setActiveTab("tokens"); if (!tokenData && !tokenLoading) loadTokenUsage(tokenFrom, tokenTo); }}
            >Token Usage</button>
          </div>

          {activeTab === "tenants" && (<>
          {/* Add tenant */}
          <div className="sa-card sa-card--form">
            <h2 className="sa-card-title">Add a tenant</h2>
            <form onSubmit={handleCreate}>
              <div className="sa-grid">
                <div>
                  <label className="sa-label">Tenant name *</label>
                  <input className="sa-input" value={name} onChange={e => setName(e.target.value)} placeholder="Acme Corp" />
                </div>
                <div>
                  <label className="sa-label">Tenant admin email *</label>
                  <input className="sa-input" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="admin@acme.com" />
                </div>
                <div>
                  <label className="sa-label">Sign-in method *</label>
                  <select className="sa-input" value={adminAuthProvider} onChange={e => setAdminAuthProvider(e.target.value)}>
                    <option value="local">Password (Keycloak)</option>
                    <option value="google">Google</option>
                  </select>
                </div>
                <div>
                  <label className="sa-label">Subscription plan *</label>
                  <select className="sa-input" value={plan} onChange={e => setPlan(e.target.value)}>
                    {Object.keys(plans).map(k => (
                      <option key={k} value={k}>
                        {plans[k].label} — {plans[k].maxUsers} users · {plans[k].durationDays} days
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="sa-label">Description</label>
                  <input className="sa-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
                </div>
              </div>

              {plans[plan] && (
                <div className="sa-hint">
                  <strong>{plans[plan].label}</strong>: up to <strong>{plans[plan].maxUsers}</strong> users, valid <strong>{plans[plan].durationDays}</strong> days from creation.
                </div>
              )}

              {formError && <div className="sa-error">{formError}</div>}
              {formSuccess && <div className="sa-success">{formSuccess}</div>}

              <button type="submit" className="sa-btn" disabled={submitting}>
                {submitting ? "Creating…" : "Create tenant & invite admin"}
              </button>
            </form>
          </div>

          {/* Tenants table */}
          <div className="sa-card sa-card--table">
            <div className="sa-table-head">
              <h2 className="sa-card-title" style={{ margin: 0 }}>Tenants</h2>
              <button className="sa-refresh" onClick={loadTenants}>Refresh</button>
            </div>
            {error && <div className="sa-error" style={{ padding: 16, margin: 0 }}>{error}</div>}
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Plan</th>
                    <th>Seats</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Status</th>
                    <th>Manage</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7}>Loading…</td></tr>
                  ) : tenants.length === 0 ? (
                    <tr><td colSpan={7}>No tenants yet. Add one above.</td></tr>
                  ) : tenants.map(t => {
                    const used = (t.usersCount || 0) + (t.pendingInvites || 0);
                    return (
                      <tr key={t._id}>
                        <td>
                          <div className="sa-tenant-name">{t.name || "—"}</div>
                          {t.description && <div className="sa-tenant-desc">{t.description}</div>}
                        </td>
                        <td>{plans[t.plan]?.label || t.plan || "—"}</td>
                        <td>
                          {used}{t.maxUsers != null ? ` / ${t.maxUsers}` : ""}
                          {t.pendingInvites ? <span className="sa-seats-pending"> ({t.pendingInvites} pending)</span> : null}
                        </td>
                        <td>{fmtDate(t.createdAt || t.subscriptionStart)}</td>
                        <td>{fmtDate(t.subscriptionEnd)}</td>
                        <td>{statusChip(t)}</td>
                        <td>
                          <div className="sa-actions">
                            <select
                              className="sa-plan-select"
                              value={planChoice[t._id] ?? t.plan ?? ""}
                              disabled={rowBusy[t._id]}
                              onChange={(e) => { const value = e.target.value; setPlanChoice((p) => ({ ...p, [t._id]: value })); }}>
                              {Object.keys(plans).map((k) => (
                                <option key={k} value={k}>{plans[k].label}</option>
                              ))}
                            </select>
                            <button className="sa-mini" disabled={rowBusy[t._id]}
                              onClick={() => patchTenant(t._id, { plan: planChoice[t._id] ?? t.plan })}>
                              Change plan
                            </button>
                            <button className="sa-mini" disabled={rowBusy[t._id]}
                              onClick={() => patchTenant(t._id, { extendDays: 30 })}>
                              +30d
                            </button>
                            {t.status === "suspended" ? (
                              <button className="sa-mini" disabled={rowBusy[t._id]}
                                onClick={() => patchTenant(t._id, { status: "active" })}>
                                Reactivate
                              </button>
                            ) : (
                              <button className="sa-mini sa-mini--warn" disabled={rowBusy[t._id]}
                                onClick={() => patchTenant(t._id, { status: "suspended" })}>
                                Suspend
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          </>)}

          {/* Users — move a user to another tenant */}
          {activeTab === "users" && (
          <div className="sa-card sa-card--table">
            <div className="sa-table-head">
              <h2 className="sa-card-title" style={{ margin: 0 }}>Users</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  className="sa-input"
                  style={{ maxWidth: 220 }}
                  placeholder="Filter by name / email"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                />
                <button className="sa-refresh" onClick={loadUsers}>Refresh</button>
              </div>
            </div>
            {usersError && <div className="sa-error" style={{ padding: 16, margin: 0 }}>{usersError}</div>}
            <div className="sa-table-wrap">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Current tenant</th>
                    <th>Tenant role</th>
                    <th>Status</th>
                    <th>Move to tenant</th>
                  </tr>
                </thead>
                <tbody>
                  {usersLoading ? (
                    <tr><td colSpan={5}>Loading…</td></tr>
                  ) : (() => {
                    const q = userFilter.trim().toLowerCase();
                    const rows = q
                      ? users.filter(u => [u.username, u.email, u.firstName, u.lastName].filter(Boolean).some(v => String(v).toLowerCase().includes(q)))
                      : users;
                    if (rows.length === 0) return <tr><td colSpan={5}>{q ? "No matching users." : "No users yet."}</td></tr>;
                    return rows.map(u => (
                      <tr key={u._id}>
                        <td>
                          <div className="sa-tenant-name">{[u.firstName, u.lastName].filter(Boolean).join(" ") || u.username || "—"}</div>
                          {u.email && <div className="sa-tenant-desc">{u.email}</div>}
                        </td>
                        <td>{tenantName(u.tenantId)}</td>
                        <td>{u.tenantRole || "—"}</td>
                        <td>{u.permission || "—"}</td>
                        <td>
                          <div className="sa-actions">
                            <select
                              className="sa-plan-select"
                              value={tenantChoice[u._id] ?? ""}
                              disabled={userRowBusy[u._id]}
                              onChange={(e) => { const value = e.target.value; setTenantChoice((p) => ({ ...p, [u._id]: value })); }}>
                              <option value="">— select tenant —</option>
                              {tenants.map((t) => (
                                <option key={t._id} value={t._id} disabled={String(t._id) === String(u.tenantId)}>
                                  {t.name}{String(t._id) === String(u.tenantId) ? " (current)" : ""}
                                </option>
                              ))}
                            </select>
                            <button className="sa-mini" disabled={userRowBusy[u._id] || !tenantChoice[u._id]}
                              onClick={() => moveUser(u._id)}>
                              {userRowBusy[u._id] ? "Moving…" : "Move"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {activeTab === "logs" && <LogDownloadCard />}

          {activeTab === "tokens" && (
            <>
              <div className="sa-card sa-card--form">
                <h2 className="sa-card-title">Token usage by tenant &amp; team</h2>
                <div className="sa-grid">
                  <div>
                    <label className="sa-label">From</label>
                    <input type="date" className="sa-input" value={tokenFrom} max={tokenTo || undefined} onChange={e => setTokenFrom(e.target.value)} />
                  </div>
                  <div>
                    <label className="sa-label">To</label>
                    <input type="date" className="sa-input" value={tokenTo} min={tokenFrom || undefined} onChange={e => setTokenTo(e.target.value)} />
                  </div>
                </div>
                <button className="sa-btn" disabled={tokenLoading} onClick={() => loadTokenUsage(tokenFrom, tokenTo)}>
                  {tokenLoading ? "Loading…" : "Apply"}
                </button>
                {tokenErr && <div className="sa-error">{tokenErr}</div>}
              </div>

              {tokenData && tokenData.totals && (
                <div className="sa-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginBottom: 24 }}>
                  <div className="sa-card sa-kpi"><div className="sa-kpi-label">Total est. cost</div><div className="sa-kpi-value">{fmtCost(tokenData.totals.estimatedCostUSD)}</div></div>
                  <div className="sa-card sa-kpi"><div className="sa-kpi-label">Total tokens</div><div className="sa-kpi-value">{fmtTokens(tokenData.totals.tokens)}</div></div>
                  <div className="sa-card sa-kpi"><div className="sa-kpi-label">Requests</div><div className="sa-kpi-value">{(tokenData.totals.requestCount || 0).toLocaleString()}</div></div>
                  <div className="sa-card sa-kpi"><div className="sa-kpi-label">Tenants</div><div className="sa-kpi-value">{tokenData.totals.tenantCount || (tokenData.byTenant || []).length}</div></div>
                </div>
              )}

              {/* Sub-tabs: By tenant / By team */}
              <div className="sa-tabs">
                <button className={`sa-tab ${tokenView === "tenant" ? "sa-tab--active" : ""}`} onClick={() => setTokenView("tenant")}>By tenant</button>
                <button className={`sa-tab ${tokenView === "team" ? "sa-tab--active" : ""}`} onClick={() => setTokenView("team")}>By team</button>
              </div>

              {tokenView === "tenant" && (
                <div className="sa-card sa-card--table">
                  <div className="sa-table-head">
                    <span>By tenant</span>
                    <button className="sa-refresh" disabled={tokenLoading} onClick={() => loadTokenUsage(tokenFrom, tokenTo)}>↻ Refresh</button>
                  </div>
                  <div className="sa-table-wrap">
                    <table className="sa-table">
                      <thead><tr><th>Tenant</th><th>Users</th><th>Requests</th><th>Input</th><th>Output</th><th>Tokens</th><th>Est. cost</th></tr></thead>
                      <tbody>
                        {tokenLoading && <tr><td colSpan={7}>Loading…</td></tr>}
                        {!tokenLoading && (!tokenData || !(tokenData.byTenant || []).length) && <tr><td colSpan={7}>No usage in this range.</td></tr>}
                        {!tokenLoading && tokenData && (tokenData.byTenant || []).map(t => (
                          <tr key={t.tenantId}>
                            <td className="sa-tenant-name">{t.name}</td>
                            <td>{t.userCount}</td>
                            <td>{(t.requestCount || 0).toLocaleString()}</td>
                            <td>{fmtTokens(t.inputTokens)}</td>
                            <td>{fmtTokens(t.outputTokens)}</td>
                            <td>{fmtTokens(t.tokens)}</td>
                            <td>{fmtCost(t.estimatedCostUSD)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tokenView === "team" && (
                <div className="sa-card sa-card--table">
                  <div className="sa-table-head">
                    <span>By team</span>
                    <button className="sa-refresh" disabled={tokenLoading} onClick={() => loadTokenUsage(tokenFrom, tokenTo)}>↻ Refresh</button>
                  </div>
                  <div className="sa-table-wrap">
                    <table className="sa-table">
                      <thead><tr><th>Team</th><th>Tenant</th><th>Users</th><th>Requests</th><th>Tokens</th><th>Est. cost</th></tr></thead>
                      <tbody>
                        {tokenLoading && <tr><td colSpan={6}>Loading…</td></tr>}
                        {!tokenLoading && (!tokenData || !(tokenData.byTeam || []).length) && <tr><td colSpan={6}>No team usage in this range.</td></tr>}
                        {!tokenLoading && tokenData && (tokenData.byTeam || []).map(t => (
                          <tr key={t.teamId}>
                            <td className="sa-tenant-name">{t.name}</td>
                            <td>{t.tenantName || "—"}</td>
                            <td>{t.userCount}</td>
                            <td>{(t.requestCount || 0).toLocaleString()}</td>
                            <td>{fmtTokens(t.tokens)}</td>
                            <td>{fmtCost(t.estimatedCostUSD)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

// ── Download session logs by user + date (super-admin only; page already gated) ──
// Uses the GCS-backed session-log APIs on intelog-be (url.backendUrl):
//   GET /api/v1/session-logs/search?username=&from=&to=  → { user, sessions[] }
//   GET /api/v1/session-logs/:sessionId                  → { records[] }
// Zips every matching session's logs into one user+date-wise archive.
function LogDownloadCard() {
  const today = new Date().toISOString().slice(0, 10);
  const [username, setUsername] = useState("");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState(false);
  const base = url.backendUrl;

  const run = async () => {
    const u = username.trim();
    if (!u) { setErr(true); setMsg("Enter a username or email."); return; }
    setBusy(true); setErr(false); setMsg("Searching…");
    try {
      const qs = new URLSearchParams({ username: u });
      if (from) qs.set("from", new Date(`${from}T00:00:00`).toISOString());
      if (to) qs.set("to", new Date(`${to}T23:59:59.999`).toISOString());
      console.log(`[session-logs][fe] search user=${u} from=${from} to=${to} → GET ${base}/api/v1/session-logs/search?${qs.toString()}`);
      const r = await fetch(`${base}/api/v1/session-logs/search?${qs.toString()}`, { headers: authHeaders() });
      console.log(`[session-logs][fe] search ← HTTP ${r.status}`);
      if (r.status === 503) { setErr(true); setMsg("Log store is not configured on the server."); return; }
      if (r.status === 404) { setErr(true); setMsg("User not found."); return; }
      const data = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(true); setMsg(data.message || `Search failed (${r.status})`); return; }
      const sessions = data.sessions || [];
      console.log(`[session-logs][fe] found ${sessions.length} session(s) for`, data.user);
      if (sessions.length === 0) { setErr(true); setMsg("No logs for this user in the selected range."); return; }

      setMsg(`Found ${sessions.length} session(s) — fetching & zipping…`);
      const zip = new JSZip();
      const all = [];
      let withLogs = 0;
      for (const s of sessions) {
        try {
          const lr = await fetch(`${base}/api/v1/session-logs/${encodeURIComponent(s.sessionId)}`, { headers: authHeaders() });
          if (!lr.ok) { console.warn(`[session-logs][fe] session=${s.sessionId} ← HTTP ${lr.status} (skipped)`); continue; }
          const body = await lr.json();
          const day = (s.createdAt ? new Date(s.createdAt) : new Date()).toISOString().slice(0, 10);
          zip.file(`${day}_${s.sessionId}.json`, JSON.stringify(body, null, 2));
          if (Array.isArray(body.records)) all.push(...body.records);
          // Rich, debuggable request/response: readable transcript + full JSONL.
          if (body.markdown) zip.file(`${day}_${s.sessionId}.audit.md`, body.markdown);
          if (Array.isArray(body.richRecords) && body.richRecords.length) {
            zip.file(`${day}_${s.sessionId}.audit.jsonl`, body.richRecords.map((r) => JSON.stringify(r)).join("\n") + "\n");
          }
          withLogs += 1;
          console.log(`[session-logs][fe] session=${s.sessionId} objects=${body.objectCount ?? '?'} records=${(body.records || []).length} richRecords=${(body.richRecords || []).length}`);
        } catch (e) { console.warn(`[session-logs][fe] session=${s.sessionId} fetch failed: ${e.message}`); }
      }
      if (withLogs === 0) { setErr(true); setMsg("No stored logs found for these sessions yet."); return; }
      console.log(`[session-logs][fe] zipping ${withLogs}/${sessions.length} session(s), ${all.length} total records`);

      all.sort((a, b) => String((a && a.ts) || "").localeCompare(String((b && b.ts) || "")));
      zip.file("all-records.json", JSON.stringify(all, null, 2));
      zip.file("manifest.json", JSON.stringify({
        user: data.user, from, to,
        sessions: sessions.length, sessionsWithLogs: withLogs,
        generatedAt: new Date().toISOString(),
      }, null, 2));

      const blob = await zip.generateAsync({ type: "blob" });
      const name = ((data.user && data.user.username) || u).replace(/[^A-Za-z0-9._-]+/g, "_");
      const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(blob), download: `${name}_${from}_${to}_logs.zip`,
      });
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      console.log(`[session-logs][fe] downloaded ${name}_${from}_${to}_logs.zip (${withLogs} session file(s))`);
      setErr(false); setMsg(`Downloaded ${withLogs} session log file(s).`);
    } catch (e) {
      console.error('[session-logs][fe] download failed:', e);
      setErr(true); setMsg(e.message || String(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="sa-card sa-card--form">
      <h2 className="sa-card-title">Download Session Logs</h2>
      <div className="sa-grid">
        <label className="sa-label">Username or email
          <input className="sa-input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="jane.doe or jane@acme.com" />
        </label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label className="sa-label">From
            <input className="sa-input" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="sa-label">To
            <input className="sa-input" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      </div>
      <button className="sa-btn" disabled={busy} onClick={run}>{busy ? "Working…" : "Download Logs"}</button>
      {msg && <div className={err ? "sa-error" : "sa-hint"}>{msg}</div>}
    </div>
  );
}

export default SuperAdmin;
